import { Col, Row } from "components/primitives";
import { usePropChangedEffect } from "hooks/propChangedEffect";
import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { WidgetFC } from "types/Widget";

export interface PlaceValueMatState {
  values: number[];
}

const MaxDecimals = 3;

const HeadersLong = [
  "Millions",
  "Hundreds of Thousands",
  "Tens of Thousands",
  "Thousands",
  "Hundreds",
  "Tens",
  "Ones",
  "Tenths",
  "Hundredths",
  "Thousandths",
];

const HeadersShort = ["M", "100 TH", "10 TH", "TH", "H", "T", "O", "t", "h", "th"];

export const ColumnMapping: { [key: string]: number } = {
  "Millions (1,000,000)": 7,
  "Hundreds of Thousands (100,000)": 6,
  "Tens of Thousands (10,000)": 5,
  "Thousands (1,000)": 4,
  "Hundreds (100)": 3,
  "Tens (10)": 2,
  "Ones (1)": 1,
  "Thousandths (0.001)": 3,
  "Hundredths (0.01)": 2,
  "Tenths (0.1)": 1,
  None: 0,
};

export const PlaceValueMat: WidgetFC<PlaceValueMatState> = ({
  state: { values },
  setState,
  settings,
}) => {
  const { short: showShort, long: showLong, cols, decimals, generate, resultFormat } = settings;
  const [selectedCol, setSelectedCol] = useState<number | undefined>();

  const decimalsCount = ColumnMapping[decimals];
  const columnCount = ColumnMapping[cols];

  const decimalStartIndex = values.length - decimalsCount;
  const resultingNumber = useCallback(getResultingNumber, [values, decimalsCount, resultFormat]);

  const boxScale =
    Math.min(10 - columnCount - decimalsCount, 10) *
    (!showShort ? 1 : 0.25) *
    (!showLong ? 1 : 0.25);
  const maxWidth = 9.5 + boxScale;

  useEffect(() => {
    document.addEventListener("keydown", getKeyDown, false);

    return () => {
      document.removeEventListener("keydown", getKeyDown, false);
    };
  }, [selectedCol]);

  // Adjust cols when cols change
  useEffect(() => {
    const numCols = columnCount;
    const newTotal = numCols + decimalsCount;

    let newValues = [...values];
    if (newTotal < values.length) {
      newValues = newValues.slice(newValues.length - newTotal, newValues.length);
    } else {
      for (let i = 0; i < newTotal - values.length; i++) {
        newValues = [0, ...newValues];
      }
    }
    setState({ values: newValues });
    setSelectedCol(undefined);
  }, [cols]);

  // Adjust cols when decimals change
  useEffect(() => {
    const numDecimals = decimalsCount;
    const newTotal = numDecimals + columnCount;

    let newValues = [...values];
    if (newTotal < values.length) {
      newValues = newValues.slice(0, newTotal);
    } else {
      for (let i = 0; i < newTotal - values.length; i++) {
        newValues = [...newValues, 0];
      }
    }
    setState({ values: newValues });
    setSelectedCol(undefined);
  }, [decimals]);

  usePropChangedEffect(() => {
    if (!isNaN(generate)) {
      let newDecimalCount = countDecimals(generate);
      // Filter and process the input. This converts it to an array,
      // removes the "." and truncates extra decimals
      let newValues = String(generate)
        .substring(
          0,
          newDecimalCount > 3 ? String(generate).length - newDecimalCount + 3 : generate.length,
        )
        .split("")
        .map((x) => Number(x))
        .filter((x) => !isNaN(x));

      newDecimalCount = Math.min(newDecimalCount, 3);
      // Pad the start of the array to match the desired column count
      const padding = columnCount - newValues.length + newDecimalCount;
      for (let i = 0; i < padding; i++) {
        newValues = [0, ...newValues];
      }
      // Pad the end of the array to match the desired decimal count
      const trailing = decimalsCount - newDecimalCount;
      for (let i = 0; i < trailing; i++) {
        newValues.push(0);
      }

      // Prevent a number larger than the specified cols and decimals from being set
      if (newValues.length - columnCount - decimalsCount > 0) {
        newValues = newValues.slice(
          newValues.length - columnCount - decimalsCount,
          newValues.length,
        );
        // As the requested value is larger than what we can show, flood with 9s
        for (let i = 0; i < newValues.length; i++) {
          newValues[i] = 9;
        }
      }
      setState({ values: newValues });
      setSelectedCol(undefined);
    }
  }, [generate]);

  function getResultingNumber() {
    const number = Number(values.join("")) / Math.pow(10, decimalsCount);
    if (resultFormat === "With Commas (1,000)") {
      return number.toLocaleString(undefined, { minimumFractionDigits: decimalsCount });
    } else {
      return number.toFixed(decimalsCount);
    }
  }

  const countDecimals = (num: number) => {
    if (Math.floor(num.valueOf()) === num.valueOf()) return 0;
    return num.toString().split(".")[1].length || 0;
  };

  const getKeyDown = (value: KeyboardEvent) => {
    const val = Number(value.key);
    if (!isNaN(val) && selectedCol !== undefined) {
      let newValues = [...values];
      newValues[selectedCol] = val;
      setState({ values: newValues });
    }
  };

  const selectColumn = (index: number) => {
    setSelectedCol(index);
  };

  return (
    <OuterContainer onKeyDown={(e) => console.log(e)}>
      <ResultNumber fontSize={3 + boxScale / 4}>{resultingNumber()}</ResultNumber>
      <HeaderContainer fontSize="3cqw">
        {showShort &&
          values.map((_, index) => (
            <NumberCell key={index} selected={false} maxWidth={maxWidth}>
              {
                HeadersShort[
                  index + (HeadersShort.length - values.length) - (MaxDecimals - decimalsCount)
                ]
              }
            </NumberCell>
          ))}
      </HeaderContainer>
      <HeaderContainer fontSize="1.25cqw">
        {showLong &&
          values.map((_, index) => (
            <NumberCell key={index} selected={false} maxWidth={maxWidth}>
              {
                HeadersLong[
                  index + (HeadersLong.length - values.length) - (MaxDecimals - decimalsCount)
                ]
              }
            </NumberCell>
          ))}
      </HeaderContainer>
      <NumberContainer>
        {values.slice(0, values.length - decimalsCount).map((value, index) => (
          <NumberCell
            key={index}
            onClick={() => selectColumn(index)}
            selected={selectedCol === index}
            maxWidth={maxWidth}
          >
            {value}
          </NumberCell>
        ))}
        {decimalsCount !== 0 && (
          <DecimalContainer>
            <DecimalPoint />
          </DecimalContainer>
        )}
        {values.slice(values.length - decimalsCount, values.length).map((value, index) => (
          <NumberCell
            key={index}
            onClick={() => selectColumn(index + decimalStartIndex)}
            selected={selectedCol === index + decimalStartIndex}
            maxWidth={maxWidth}
          >
            {value}
          </NumberCell>
        ))}
      </NumberContainer>
    </OuterContainer>
  );
};

const OuterContainer = styled(Col)`
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const NumberCell = styled(Row)<{ selected: boolean; maxWidth: number }>`
  border: 1px solid black;
  font-weight: 600;
  width: ${(props) => props.maxWidth}cqw;
  aspect-ratio: 1;
  background-color: ${(props) => (props.selected ? "var(--primary) !important" : "white")};
  color: ${(props) => props.selected && "white"};
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const HeaderContainer = styled(Row)<{ fontSize: string }>`
  color: var(--widget-primary);
  font-size: ${(props) => props.fontSize};
  width: 95%;
  justify-content: center;
  align-items: center;

  ${NumberCell} {
    line-height: 1em;
  }
`;

const NumberContainer = styled(Row)`
  width: 95%;
  justify-content: center;
  align-items: center;
  color: var(--secondary);
  font-size: 5cqw;

  ${NumberCell}:hover {
    background-color: lightgray;
  }
`;

const DecimalContainer = styled.div`
  width: 0px;
  height: 100%;
  position: relative;
`;

const DecimalPoint = styled.div`
  position: absolute;
  background-color: var(--primary);
  border-radius: 50%;
  width: 3cqw;
  height: 3cqw;
  bottom: 0px;
  transform: translate(-50%, 50%);
  border: 1px solid white;
`;

const ResultNumber = styled.div<{ fontSize: number }>`
  border-radius: 100px;
  background-color: #eaf0ff;

  font-weight: 600;
  font-size: ${(props) => props.fontSize}cqw;
  color: var(--secondary);
  padding: 1cqw 2cqw;
  margin-bottom: 1cqw;
  line-height: 1em;
`;
